import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { getLenis } from '@/components/motion/smooth-scroll'
import { navLinks, siteConfig } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const mounted = useRef(false)

    useGSAP(() => {
        const element = root.current

        if (!element) return

        const rows = element.querySelectorAll('[data-menu-row]')
        const lines = element.querySelectorAll('[data-menu-line]')
        const info = element.querySelectorAll('[data-menu-info]')
        const fast = prefersReducedMotion()

        if (!mounted.current) {
            mounted.current = true
            gsap.set(element, { autoAlpha: 0, clipPath: 'inset(0% 0% 100% 0%)' })

            return
        }

        if (open) {
            gsap.timeline()
                .set(element, { autoAlpha: 1 })
                .to(element, { clipPath: 'inset(0% 0% 0% 0%)', duration: fast ? 0 : 0.9, ease: 'expo.inOut' })
                .fromTo(lines, { scaleX: 0 }, { scaleX: 1, transformOrigin: '0% 50%', duration: fast ? 0 : 1, stagger: 0.05, ease: 'expo.inOut' }, 0.25)
                .fromTo(rows, { yPercent: 140 }, { yPercent: 0, duration: fast ? 0 : 1, stagger: 0.06, ease: 'expo.out' }, 0.35)
                .fromTo(info, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: fast ? 0 : 0.8, stagger: 0.08 }, 0.6)
        } else {
            gsap.timeline()
                .to(rows, { yPercent: -140, duration: fast ? 0 : 0.5, stagger: 0.03, ease: 'expo.in' })
                .to(element, { clipPath: 'inset(0% 0% 100% 0%)', duration: fast ? 0 : 0.8, ease: 'expo.inOut' }, 0.2)
                .set(element, { autoAlpha: 0 })
        }
    }, { scope: root, dependencies: [open], revertOnUpdate: false })

    useEffect(() => {
        if (!open) return

        getLenis()?.stop()

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKey)

        const firstLink = root.current?.querySelector<HTMLAnchorElement>('a')

        firstLink?.focus({ preventScroll: true })

        return () => {
            window.removeEventListener('keydown', onKey)

            if (!document.documentElement.classList.contains('is-transitioning')) getLenis()?.start()
        }
    }, [open, onClose])

    return (
        <div
            ref={root}
            id='mobile-menu'
            data-lenis-prevent
            aria-hidden={!open}
            className='invisible fixed inset-0 z-40 flex flex-col justify-between overflow-y-auto bg-ink pb-8 pt-[calc(var(--header-height)+24px)] gutter lg:hidden'
        >
            <nav aria-label='Mobile'>
                <ul>
                    {navLinks.map(link => (
                        <li key={link.href} className='relative'>
                            <span data-menu-line aria-hidden='true' className='absolute inset-x-0 top-0 h-px bg-line' />

                            <Link
                                href={link.href}
                                onClick={onClose}
                                tabIndex={open ? 0 : -1}
                                className='group flex items-baseline gap-4 py-4'
                            >
                                <span className='mask'>
                                    <span data-menu-row className='meta-mono text-ash'>
                                        {link.index}
                                    </span>
                                </span>

                                <span className='mask'>
                                    <span data-menu-row className='block font-heading text-[clamp(44px,13vw,88px)] uppercase leading-[0.9] text-bone transition-colors duration-300 group-active:text-acid'>
                                        {link.label}
                                    </span>
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='mt-10 flex flex-wrap items-end justify-between gap-4 border-t border-line pt-6'>
                <a
                    data-menu-info
                    href={`mailto:${siteConfig.email}`}
                    tabIndex={open ? 0 : -1}
                    className='serif-m text-bone'
                >
                    {siteConfig.email}
                </a>

                <p data-menu-info className='label-mono text-ash'>
                    {siteConfig.season}
                </p>
            </div>
        </div>
    )
}

export default MobileMenu
